import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Bell, ChevronDown, ChevronUp, Settings } from 'lucide-react';
import { ReactComponent as PigeonsLogotype } from '../../assets/images/pigeons_logotype1.svg';
import { useProfileStore } from '../../stores/profileStore';
import { useTranslation } from 'react-i18next';
import { Button } from '../common/Button';
import { useUIStore } from '../../stores/uiStore';
import { ROLES } from '../../types/auth';
import ProfileAvatar from '../common/ProfileAvatar';
import LanguageSwitcher from '../LanguageSwitcher';

interface DesktopNavigationProps {
  isAccountMenuOpen: boolean;
  onSignOut: () => void;
  onAccountMenuToggle: (forceClose?: boolean) => void;
  onCloseMenu: () => void;
  notifications: number;
}

export const DesktopNavigation: React.FC<DesktopNavigationProps> = ({
  isAccountMenuOpen,
  onSignOut,
  onAccountMenuToggle,
  onCloseMenu,
  notifications,
}) => {
  const { profile } = useProfileStore();
  const { t } = useTranslation('common');
  const { hideNavigationElements } = useUIStore();
  const location = useLocation();
  const initials = `${profile?.first_name?.[0] || ''}${profile?.last_name?.[0] || ''}`;

  const isOnFreelancersPage = location.pathname === '/freelancers';
  const isOnJobSearchPage = location.pathname === '/job-search';

  return (
    <div className="items-center justify-between hidden w-full px-8 md:flex">
      <div className="flex items-center flex-1 gap-8">
        <Link to="/" className="flex-shrink-0 text-2xl font-bold">
          <PigeonsLogotype className="h-8" aria-label="Pigeons Logo" />
        </Link>
      </div>

      <div className="flex items-center justify-end flex-shrink-0 gap-4">
        {profile && profile.role !== 'administrator' && !hideNavigationElements && (
          <div className="flex gap-2">
            {profile.role === ROLES.FREELANCER && !isOnFreelancersPage && (
              <Button
                variant="transparent-with-border"
                className="whitespace-nowrap"
                href="/freelancers"
              >
                {t('navigation.findFreelancer')}
              </Button>
            )}

            {((profile.role === ROLES.RECRUITER && !isOnFreelancersPage) ||
              (profile.role !== ROLES.RECRUITER && !isOnJobSearchPage)) && (
              <Button
                variant="primary"
                className="whitespace-nowrap"
                href={profile.role === ROLES.RECRUITER ? '/freelancers' : '/job-search'}
              >
                {profile.role === ROLES.RECRUITER
                  ? t('navigation.findFreelancer')
                  : t('navigation.findProject')}
              </Button>
            )}
          </div>
        )}
        <LanguageSwitcher />
        <div
          className={`flex flex-col items-center gap-6 relative p-2 ${
            isAccountMenuOpen ? 'bg-white border-2xl rounded-tl-3xl rounded-tr-3xl' : ''
          } ${profile ? '' : 'hidden'}`}
        >
          <div className="relative flex items-center gap-5">
            {!hideNavigationElements && (
              <Link
                to="/notifications"
                className="relative flex items-center justify-center mr-5 transition rounded-full h-11 w-11 hover:bg-gray-200"
              >
                <Bell className="h-7 w-7 text-secondary" />
                {notifications > 0 && (
                  <span className="absolute -right-1 -top-1 rounded-full bg-primary px-1.5 py-0.5 text-xs text-lightest">
                    {notifications}
                  </span>
                )}
              </Link>
            )}

            <div className="flex items-center gap-2">
              <button
                className="flex items-center justify-between min-w-36 font-bold transition border-2 border-transparent text-secondary focus:outline-none"
                onClick={() => onAccountMenuToggle()}
                aria-haspopup="true"
                aria-expanded={isAccountMenuOpen}
              >
                <div className="flex items-center gap-1 mr-3">
                  <span className="font-bold font-ambit text-secondary">{profile?.first_name}</span>
                  <span>
                    {isAccountMenuOpen ? (
                      <ChevronDown className="w-4 h-4 text-secondary" />
                    ) : (
                      <ChevronUp className="w-4 h-4 text-secondary" />
                    )}
                  </span>
                </div>
                <ProfileAvatar
                  profile_id={profile?.id}
                  first_name={profile?.first_name ?? ''}
                  avatar_url={profile?.avatar_url}
                  initials={initials}
                  className="h-10 w-10"
                />
              </button>
            </div>
          </div>

          {isAccountMenuOpen && (
            <div className="absolute right-0 z-40 flex flex-col w-full gap-2 p-3 bg-white border-2xl animate-fade-in top-12 rounded-bl-3xl rounded-br-3xl">
              <Link
                to="/settings"
                className="flex items-center w-full gap-2 px-3 py-2 text-base font-bold text-center transition rounded-xl text-secondary hover:bg-gray-100"
                onClick={() => onCloseMenu()}
              >
                <Settings className="w-5 h-5" />
                {t('accountCard.settings')}
              </Link>

              <Button
                className="w-full mt-3 transition text-primary-inverse"
                variant="primary"
                onClick={onSignOut}
              >
                {t('accountCard.signOut')}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
